<ng-container *ngIf="visible">
  <button class="aw-hint" type="button" *ngIf="bubble && !open && !tucked" (click)="toggle()" tabindex="-1" aria-hidden="true"
    [style.animation-duration.ms]="bubbleMs">{{ bubbleText }}</button>
  <button class="aw-fab" type="button" (click)="toggle()" [class.is-open]="open" [class.is-tucked]="tucked && !open"
    [attr.aria-expanded]="open" aria-controls="pla-assistant-panel" [attr.aria-label]="open ? 'Close Plato' : 'Ask Plato'">
    <!-- one Plato at a time: in the corner when closed, in the conversation when open -->
    <img *ngIf="!open" class="aw-fab-plato" [class.is-gone]="platoGone" [src]="moodSrc" alt="" />
    <span class="aw-fab-x" *ngIf="open" aria-hidden="true">×</span>
    <span class="aw-fab-t" *ngIf="!open" aria-hidden="true">Ask Plato</span>
  </button>

  <section class="aw-panel" id="pla-assistant-panel" *ngIf="open" [class.is-night]="!!nightColour" [style.--aw-night]="nightColour" (pointerdown)="noteActivity()" role="dialog" aria-label="Plato, the PlateX assistant">
    <header class="aw-head">
      <div class="aw-head-t">
        <div class="aw-title">Plato</div>
        <div class="aw-sub">{{ apprentice ? "PlateX's AI apprentice · beta" : "PlateX assistant · beta · dev2 admins only" }}</div>
      </div>
      <button class="aw-x" type="button" (click)="toggle()" aria-label="Close">×</button>
    </header>

    <!-- One Plato: he stands at the bottom-left of the conversation and the bubbles scroll up past him -->
    <div class="aw-bodywrap">
      <div class="aw-body" #scroller [class.is-empty]="!messages.length">
        <div class="aw-empty" *ngIf="!messages.length">
          <p *ngIf="halloweenGreeting; else plainHello">{{ halloweenGreeting }}</p>
          <ng-template #plainHello>
            <p *ngIf="!costumeIntroRunning && apprentice">Hi, I'm Plato, PlateX's AI apprentice. I can find plates for a name or word, explain how a plate reads, and show plates by series. I'm still learning: I don't value plates yet, and I'll say when I'm not sure.</p>
            <p *ngIf="!costumeIntroRunning && !apprentice">Hi, I'm Plato. Ask about a plate's value, find plates for a name, check live auctions, or ask how buying and selling works.</p>
          </ng-template>
          <button class="aw-chip" type="button" *ngFor="let s of suggestions" (click)="ask(s)">{{ s }}</button>
        </div>

        <div class="aw-msg" *ngFor="let m of messages" [class.is-user]="m.role === 'user'"
          [class.is-error]="m.error" [class.is-hidden]="!m.html">
          <div class="aw-col" *ngIf="m.html">
            <div class="aw-bubble" [innerHTML]="m.html" (click)="onBubbleClick($event)"></div>
            <!-- plate cards (e.g. comparable sales) -->
            <div class="aw-cards" *ngIf="m.cards?.length">
              <ng-container *ngFor="let c of m.cards">
              <div class="aw-card-group" *ngIf="c.groupStart">{{ c.groupStart }}</div>
              <button class="aw-card" type="button" (click)="openPlate(c.registration)">
                <span class="aw-card-plate">
                  <pla-plate [rawPlate]="spaced(c.registration)" [customClass]="'flat shaded'" [plateSize]="'x-small'"
                    [mobileSmall]="true" [openModal]="false" [clickThrough]="false" [showPrice]="false"
                    [center]="true" [noMargin]="true"></pla-plate>
                </span>
                <span class="aw-card-price">{{ c.price }}</span>
                <span class="aw-card-date">{{ monthYear(c.sold_date) }}</span>
              </button>
              </ng-container>
            </div>
            <!-- live auction lots (DVLA + Collecting Cars) with a live countdown -->
            <div class="aw-cards" *ngIf="m.lots?.length">
              <button class="aw-card aw-lot" type="button" *ngFor="let l of m.lots" (click)="openAuction(l)"
                [class.is-closing]="closingSoon(l)">
                <span class="aw-card-plate">
                  <pla-plate [rawPlate]="spaced(l.registration)" [customClass]="'flat shaded'" [plateSize]="'x-small'"
                    [mobileSmall]="true" [openModal]="false" [clickThrough]="false" [showPrice]="false"
                    [center]="true" [noMargin]="true"></pla-plate>
                </span>
                <span class="aw-card-price">{{ lotPrice(l) }}</span>
                <span class="aw-card-date">{{ lotWhen(l) }}</span>
                <span class="aw-lot-meta">{{ l.house }}<ng-container *ngIf="l.all_in"> · {{ l.all_in }} all-in</ng-container><ng-container *ngIf="l.reserve"> · {{ l.reserve }}</ng-container></span>
              </button>
              <div class="aw-lot-note" *ngIf="m.lotsNote">{{ m.lotsNote }}</div>
            </div>
            <!-- follow-up actions (client-side, instant) -->
            <div class="aw-chips" *ngIf="m.followups && !busy">
              <button type="button" class="aw-chip aw-chip--inline" *ngFor="let f of m.followups.items"
                [disabled]="f.used" (click)="runFollowup(m, f)">{{ f.label }}</button>
            </div>
          </div>
        </div>

        <div class="aw-msg aw-status" *ngIf="showStatusRow" aria-live="polite">
          <span class="aw-status-t"><span class="aw-dots" aria-hidden="true"><i></i><i></i><i></i></span>{{ statusText }}</span>
        </div>
      </div>
      <div class="aw-stage" [class.is-hero]="!messages.length" [class.is-big]="bigMood" aria-hidden="true">
        <div class="aw-svg" #stageSvg></div>
      </div>
    </div>

    <form class="aw-input" (submit)="$event.preventDefault(); ask()">
      <textarea #input rows="1" [(ngModel)]="draft" name="q" maxlength="1000"
        [placeholder]="inputHint" (keydown)="onKey($event)"
        (focus)="inputFocused = true" (blur)="inputFocused = false"></textarea>
      <button type="submit" [disabled]="busy || !draft.trim()" aria-label="Send">➤</button>
    </form>
    <div class="aw-foot">{{ apprentice ? "Plato can make mistakes. Check prices and details on the plate page." : "Valuations are indicative, not formal appraisals." }}</div>
  </section>
</ng-container>
